/**
 * qg-analytics.js: window.qgPublish(name, payload) for QualGear qg_* custom events.
 *
 * Decision GA4-CUSTOM-EV, option B "signed tokens" (owner 2026-10-06). Spec:
 * theme/docs/analytics/custom-events-ga4-2026-10-05.md section 5, Rev 2.
 *
 * The GA4 relay pixel runs in Shopify's sandbox, which may not call the shop's own origin
 * and sends Origin: null, so the Worker cannot authenticate it by origin. This first-party
 * script reads the visitor's GA client_id from the `_ga` cookie, asks the App Proxy
 * (/apps/qg/ga4/token, HMAC-signed by Shopify) for a short-lived token bound to that
 * client_id, and adds it to each publish as `_qgt`. The pixel forwards it as `token`.
 *
 * Rules: no dependencies, never throws, sends no PII (only the GA client_id the GA cookie
 * already holds). Publishes are queued while the token loads and released after at most
 * 3 s WITHOUT a token, so tracking behaviour never breaks. No `_ga` cookie (no consent or
 * no GA) means publish without a token; the pixel drops those anyway.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || typeof window.qgPublish === 'function') return;

  var TOKEN_URL = '/apps/qg/ga4/token';
  var STORE_KEY = 'qg_ga4t';
  var MAX_WAIT_MS = 3000;
  var REFRESH_S = 300; // refresh when fewer than 5 minutes remain
  var RETRY_MS = 60000; // after a failed token request, do not retry for 60 s
  var CID_RE = /(?:^|;\s*)_ga=GA\d\.\d+\.(\d{1,20}\.\d{1,20})(?:;|$)/;

  var queue = [];
  var inflight = false;
  var timer = null;
  var failCid = null;
  var failUntil = 0;
  var memo = null; // in-memory copy for when sessionStorage is unavailable

  function nowS() { return Math.floor(Date.now() / 1000); }

  function readCid() {
    try { var m = CID_RE.exec(document.cookie || ''); return m ? m[1] : null; } catch (e) { return null; }
  }

  function load() {
    try {
      var raw = window.sessionStorage.getItem(STORE_KEY);
      if (raw) {
        var o = JSON.parse(raw);
        if (o && typeof o.t === 'string' && typeof o.exp === 'number' && typeof o.cid === 'string') return o;
      }
    } catch (e) { /* storage blocked: fall back to memory */ }
    return memo;
  }

  function save(o) {
    memo = o;
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify(o)); } catch (e) { /* noop */ }
  }

  function send(name, payload, t) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        var data = Object.assign({}, payload || {});
        if (t) data._qgt = t;
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) { /* noop */ }
  }

  function flush(t) {
    if (timer) { clearTimeout(timer); timer = null; }
    var q = queue;
    queue = [];
    for (var i = 0; i < q.length; i++) send(q[i][0], q[i][1], t);
  }

  function requestToken(cid) {
    inflight = true;
    var got = null;
    function finish() {
      inflight = false;
      if (!got) { failCid = cid; failUntil = Date.now() + RETRY_MS; }
      flush(got ? got.t : null);
    }
    try {
      fetch(TOKEN_URL + '?cid=' + encodeURIComponent(cid), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { return r && r.ok ? r.json() : null; })
        .then(function (j) {
          if (j && typeof j.t === 'string' && j.t.length <= 200 && typeof j.exp === 'number') {
            got = { t: j.t, exp: j.exp, cid: cid };
            save(got);
          }
        })
        .catch(function () { /* noop */ })
        .then(finish, finish);
    } catch (e) {
      finish();
    }
  }

  window.qgPublish = function (name, payload) {
    try {
      var cid = readCid();
      if (!cid) { send(name, payload, null); return; }
      var tok = load();
      var usable = tok && tok.cid === cid && tok.exp - nowS() > 60 ? tok.t : null;
      var fresh = usable && tok.exp - nowS() > REFRESH_S;
      var retryBlocked = failCid === cid && Date.now() < failUntil;
      if (fresh || (usable && (inflight || retryBlocked))) { send(name, payload, usable); return; }
      if (usable) { send(name, payload, usable); requestToken(cid); return; } // near expiry: refresh in background
      if (retryBlocked) { send(name, payload, null); return; }
      queue.push([name, payload]);
      if (!timer) timer = setTimeout(function () { timer = null; flush(null); }, MAX_WAIT_MS);
      if (!inflight) requestToken(cid);
    } catch (e) {
      send(name, payload, null);
    }
  };
})();
